/**
 * CR313: Feature Flag Drawer
 * 
 * Details panel for editing a flag with type-specific editors.
 */

import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Slider } from '@/components/ui/slider';
import { Copy, Trash2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { FeatureFlagState } from '@/hooks/admin/useFeatureFlagsAdmin';
import type { RolloutValue } from '@/lib/admin/featureFlagsRegistry';
import { cn } from '@/lib/utils';

interface FeatureFlagDrawerProps {
  flag: FeatureFlagState | null;
  draftValue: unknown;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onValueChange: (value: unknown) => void;
  onDeleteOverride: () => void;
}

export function FeatureFlagDrawer({
  flag,
  draftValue,
  open,
  onOpenChange,
  onValueChange,
  onDeleteOverride,
}: FeatureFlagDrawerProps) {
  const { toast } = useToast();
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  
  const registryEntry = flag?.registryEntry;
  
  // Sync JSON text when flag changes
  useEffect(() => {
    if (registryEntry?.type === 'json' && draftValue !== undefined) {
      setJsonText(JSON.stringify(draftValue, null, 2));
      setJsonError(null);
    }
  }, [flag?.key, registryEntry?.type]);
  
  if (!flag || !registryEntry) return null;
  
  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(registryEntry.key);
    toast({ title: 'Key copied', description: registryEntry.key });
  };
  
  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onValueChange(parsed);
    } catch (e) {
      setJsonError('Invalid JSON');
    }
  };
  
  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch {
      setJsonError('Cannot format: invalid JSON');
    }
  };
  
  // Rollout helpers
  const rolloutValue = (registryEntry.type === 'rollout' ? draftValue : null) as RolloutValue | null;
  
  const updateRollout = (updates: Partial<RolloutValue>) => {
    const current = rolloutValue || { mode: 'OFF', percent: 0, allowlist: [] };
    onValueChange({ ...current, ...updates });
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {registryEntry.label}
            <Badge variant="outline" className="text-xs font-normal">
              {registryEntry.type}
            </Badge>
          </SheetTitle>
          <SheetDescription>{registryEntry.description}</SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Key */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Key</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-2 py-1 bg-muted rounded text-sm font-mono truncate">
                {registryEntry.key}
              </code>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCopyKey}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          
          {/* Category */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Badge variant="secondary">{registryEntry.category}</Badge>
          </div>
          
          {/* Values section */}
          <div className="border-t pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Default Value</div>
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  {JSON.stringify(registryEntry.defaultValue)}
                </code>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <Badge variant={flag.isOverridden ? 'default' : 'secondary'} className={cn(
                  flag.isOverridden && 'bg-amber-500'
                )}>
                  {flag.isOverridden ? 'Overridden' : 'Default'}
                </Badge>
              </div>
            </div>
          </div>
          
          {/* Type-specific editor */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium">Value</Label>
            
            {/* Boolean editor */}
            {registryEntry.type === 'boolean' && (
              <div className="mt-3 flex items-center justify-between p-3 border rounded-md">
                <span className="text-sm">
                  {draftValue === true ? 'Enabled' : 'Disabled'}
                </span>
                <Switch
                  checked={draftValue === true}
                  onCheckedChange={(checked) => onValueChange(checked)}
                />
              </div>
            )}
            
            {/* Rollout editor */}
            {registryEntry.type === 'rollout' && rolloutValue && (
              <div className="mt-3 space-y-4">
                {/* Mode */}
                <div className="space-y-1">
                  <Label className="text-xs">Mode</Label>
                  <Select
                    value={rolloutValue.mode}
                    onValueChange={(mode) => updateRollout({ mode: mode as RolloutValue['mode'] })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OFF">OFF</SelectItem>
                      <SelectItem value="ON">ON (100%)</SelectItem>
                      <SelectItem value="PERCENT">Percent</SelectItem>
                      <SelectItem value="ALLOWLIST">Allowlist</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Percent slider */}
                {rolloutValue.mode === 'PERCENT' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Percent</Label>
                      <span className="text-sm font-mono">{rolloutValue.percent}%</span>
                    </div>
                    <Slider
                      value={[rolloutValue.percent]}
                      onValueChange={([val]) => updateRollout({ percent: val })}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                )}
                
                {/* Allowlist */}
                {rolloutValue.mode === 'ALLOWLIST' && (
                  <div className="space-y-2">
                    <Label className="text-xs">Allowlist (one per line)</Label>
                    <Textarea
                      value={rolloutValue.allowlist.join('\n')}
                      onChange={(e) => {
                        const list = e.target.value.split('\n').filter(Boolean);
                        updateRollout({ allowlist: list });
                      }}
                      placeholder="user-uuid-1&#10;user-uuid-2"
                      className="font-mono text-xs h-24"
                    />
                    <p className="text-xs text-muted-foreground">
                      {rolloutValue.allowlist.length} entries
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* JSON editor */}
            {registryEntry.type === 'json' && (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={jsonText}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  className={cn(
                    'font-mono text-xs h-48',
                    jsonError && 'border-destructive'
                  )}
                />
                {jsonError && (
                  <p className="text-xs text-destructive">{jsonError}</p>
                )}
                <Button variant="outline" size="sm" onClick={handleFormatJson}>
                  Format JSON
                </Button>
              </div>
            )}
          </div>
          
          {/* Danger zone */}
          {flag.isOverridden && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <AlertTriangle className="h-4 w-4" />
                Danger Zone
              </div>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Override (Revert to Default)
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Override?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove the stored override for <strong>{registryEntry.key}</strong> and 
                      revert to the default value: <code>{JSON.stringify(registryEntry.defaultValue)}</code>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDeleteOverride}>
                      Delete Override
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
